// 경량 i18n — 외부 라이브러리 0
//   - Provider + useT() hook + localStorage 영속화
//   - 사전 누락 키는 dev에서 console.warn (운영 빌드엔 코드 자체가 dead-code)
//   - {{name}} 보간 지원
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ko, type TranslationKey } from '../locales/ko'
import { en } from '../locales/en'

export type Lang = 'ko' | 'en'
const STORAGE_KEY = 'mes_lang'
const SUPPORTED: Lang[] = ['ko', 'en']

const DICT: Record<Lang, Record<string, string>> = { ko, en }

interface I18nCtx {
  lang: Lang
  setLang: (l: Lang) => void
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string
}

const Ctx = createContext<I18nCtx>({
  lang: 'ko',
  setLang: () => {},
  t: (k) => String(k),
})

function detectInitial(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && SUPPORTED.includes(stored as Lang)) return stored as Lang
  } catch { /* private mode */ }
  if (typeof navigator !== 'undefined') {
    const nav = navigator.language?.toLowerCase() ?? ''
    if (nav.startsWith('ko')) return 'ko'
    if (nav.startsWith('en')) return 'en'
  }
  return 'ko'
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{{${k}}}`,
  )
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectInitial())

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang) } catch { /* ignore */ }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang
    }
  }, [lang])

  const setLang = useCallback((l: Lang) => {
    if (SUPPORTED.includes(l)) setLangState(l)
  }, [])

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      const dict = DICT[lang]
      const value = dict[key]
      if (value === undefined) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[i18n] missing key "${key}" for lang "${lang}"`)
        }
        // 폴백: ko → key 그대로
        const fallback = DICT.ko[key]
        return interpolate(fallback ?? String(key), vars)
      }
      return interpolate(value, vars)
    },
    [lang],
  )

  const value = useMemo<I18nCtx>(() => ({ lang, setLang, t }), [lang, setLang, t])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useT(): I18nCtx {
  return useContext(Ctx)
}

/** Bare util — fetch 요청에 Accept-Language 헤더 자동 부착용 */
export function currentLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && SUPPORTED.includes(stored as Lang)) return stored as Lang
  } catch { /* ignore */ }
  return 'ko'
}
